import type { TvLineupChannel } from "../../../hooks/useTvLineup";
import type { GuideCopy } from "./guideCopy";
import type { GuideScreenStyle } from "./GuideScreen";
import { eventClock, progressOf, type NowNext } from "./lineup";

/**
 * The strip that appears on a channel change and fades (`TV-4`).
 *
 * It is the only thing on screen during the one interaction a guest performs
 * dozens of times an evening, so it answers all three questions a zap raises at
 * once — which channel is this, what is on, and what is next — and then gets out
 * of the way. It never waits for the picture: it describes the channel being
 * AIMED at, which is why holding ch+ reads as a list flicking past rather than
 * as a television that has stopped responding.
 *
 * ⚠️ **A channel with no guide says why, here.** This is the surface where
 * `BE-22`'s silent unmapped channel becomes a sentence — the bar is what a guest
 * looks at, so an explanation anywhere else is an explanation nobody reads.
 */

interface Props {
  style: GuideScreenStyle;
  copy: GuideCopy;
  channel: TvLineupChannel;
  info: NowNext;
  now: Date;
  /** Fades out rather than unmounting, so the picture behind it is never cut. */
  visible: boolean;
  /** A sentence about the playback attempt itself, when there is one. */
  note?: string | null;
  /** The host's inner padding, so the bar sits inside the TV-safe area. */
  pad: { x: number; y: number };
}

export function ChannelBar({
  style,
  copy,
  channel,
  info,
  now,
  visible,
  note,
  pad,
}: Props) {
  const progress = info.current ? progressOf(info.current, now) : 0;
  return (
    <div
      data-testid="channel-bar"
      data-visible={visible ? "true" : "false"}
      style={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        padding: `48px ${pad.x}px ${pad.y}px`,
        display: "flex",
        alignItems: "flex-end",
        gap: 36,
        color: style.ink,
        fontFamily: style.font,
        background:
          "linear-gradient(to top, rgba(0,0,0,0.86) 0%, rgba(0,0,0,0.62) 55%, rgba(0,0,0,0) 100%)",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(24px)",
        transition: "opacity 320ms ease, transform 320ms ease",
        pointerEvents: "none",
      }}
    >
      <div
        data-testid="channel-bar-number"
        style={{
          minWidth: 132,
          fontFamily: style.titleFont,
          fontSize: 86,
          // 1.2, not tighter: this box is bounded and a tight line-height inside
          // a bounded box is how descenders get sliced off.
          lineHeight: 1.2,
          color: style.accent,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {channel.number}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          data-testid="channel-bar-title"
          style={{
            fontFamily: style.titleFont,
            fontSize: 44,
            lineHeight: 1.2,
            marginBottom: 10,
          }}
        >
          {channel.title || `${copy.channel} ${channel.number}`}
        </div>

        {info.current ? (
          <>
            <div
              data-testid="channel-bar-now"
              style={{ fontSize: 30, lineHeight: 1.3 }}
            >
              <span style={{ color: style.accent, marginRight: 14 }}>
                {copy.now}
              </span>
              {info.current.title}
              <span style={{ color: style.inkDim, marginLeft: 16 }}>
                {eventClock(info.current)}
              </span>
            </div>
            <div
              aria-hidden
              style={{
                height: 6,
                borderRadius: 3,
                background: style.rule,
                margin: "14px 0 12px",
                maxWidth: 760,
                overflow: "hidden",
              }}
            >
              <div
                data-testid="channel-bar-progress"
                data-progress={progress.toFixed(2)}
                style={{
                  width: `${Math.round(progress * 100)}%`,
                  height: "100%",
                  background: style.accent,
                }}
              />
            </div>
          </>
        ) : (
          // The unmapped / gap case. Two lines, because "no guide" alone reads
          // as a fault: the second line says why, and that it still plays.
          <div data-testid="channel-bar-noguide" style={{ marginBottom: 6 }}>
            <div style={{ fontSize: 30, lineHeight: 1.3 }}>
              {info.state === "unmapped" ? copy.noGuide : copy.noProgrammeNow}
            </div>
            <div
              data-testid="channel-bar-noguide-why"
              style={{ fontSize: 24, lineHeight: 1.35, color: style.inkDim }}
            >
              {info.state === "unmapped"
                ? copy.noGuideWhy
                : copy.gapFor(gapMinutes(info, now))}
            </div>
          </div>
        )}

        <div
          data-testid="channel-bar-next"
          style={{ fontSize: 24, lineHeight: 1.35, color: style.inkDim }}
        >
          {info.next ? (
            <>
              <span style={{ marginRight: 12 }}>{copy.next}</span>
              {info.next.title}
              <span style={{ marginLeft: 14 }}>
                {eventClock(info.next).split(" – ")[0]}
              </span>
            </>
          ) : (
            copy.guideEnds
          )}
        </div>

        {note ? (
          <div
            data-testid="channel-bar-note"
            style={{
              marginTop: 14,
              fontSize: 26,
              lineHeight: 1.35,
              color: style.accent,
            }}
          >
            {note}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * How much of the hole is left, in minutes, counted FROM NOW rather than from
 * its start — a guest does not care that the hole began three hours ago.
 */
export function gapMinutes(info: NowNext, now: Date): number {
  if (!info.gap) return 0;
  const to = Date.parse(info.gap.to);
  if (!Number.isFinite(to)) return Math.round(info.gap.seconds / 60);
  return Math.max(0, Math.round((to - now.getTime()) / 60_000));
}
